import { useEffect } from 'react';
import { getSocket } from '../../../lib/socket';
import { SOCKET_EVENTS } from '../../../lib/socketEvents';
import {
  patchMessageReadCountsByIds,
} from '../../common/chat/reactions/readReceiptRealtimeUtils';
import { mergeMessages, removeMessages } from '../../common/chat/timeline/timelineMerge';
import { makeLinkPreviewReadyHandler } from '../../common/chat/timeline/linkPreviewRealtime';
import {
  GROUP_CHAT_PAGE_SIZE,
  buildRealtimeThreadTextMessage,
  groupPaneDbg,
  pickRealtimeThreadId,
} from './artistGroupChatPaneUtils';

export default function useArtistGroupChatSocket({
  chatTopics, listArtistGroupChatMessages, markArtistGroupChatMessagesRead,
  loadMessages,
  loadThread,
  onThreadMeta,
  onThreadsRefresh,
  refreshAnnouncements,
  refreshMessageReactionDetails,
  setMessages,
  threadId,
}) {
  useEffect(() => {
    if (!threadId) return undefined;
    const socket = getSocket();
    if (!socket.connected) {
      try {
        socket.connect();
      } catch (_) {}
    }

    socket.emit(SOCKET_EVENTS.JOIN_GROUP_CHAT_THREAD, threadId);
    groupPaneDbg('socket:join-thread', { threadId });

    // §3.3 RECONNECT: 再接続時に開いているグループスレッドを最新へ再同期(silent)。
    // onNewMessage のスケルトン経路(最新ページを silent fetch して merge)と同じ既存ロジックを
    // 再利用する。このフックはスレッドが選択されている間のみマウントされるため常に対象。
    const handleConnect = () => {
      groupPaneDbg('socket:reconnect-resync', { threadId });
      (async () => {
        try {
          const data = await listArtistGroupChatMessages(threadId, 0, GROUP_CHAT_PAGE_SIZE);
          const rows = Array.isArray(data?.messages) ? data.messages : [];
          if (rows.length) setMessages((prev) => mergeMessages(prev, rows));
        } catch (_) {}
      })();
    };

    const onNewMessage = (payload = {}) => {
      if (String(pickRealtimeThreadId(payload) || '') !== String(threadId)) return;
      groupPaneDbg('socket:new-message', {
        threadId,
        messageId: payload?.messageId || null,
      });
      chatTopics.refresh();
      const realtimeMessage = buildRealtimeThreadTextMessage(payload);
      if (chatTopics.topicId && String(realtimeMessage?.topic_id || realtimeMessage?.topic?.id || '') !== chatTopics.topicId) return;
      if (realtimeMessage) {
        setMessages((prev) => mergeMessages(prev, [realtimeMessage]));
        markArtistGroupChatMessagesRead({
          threadId,
          lastReadMessageId: realtimeMessage.id,
        }).catch(() => {});
        if (typeof onThreadMeta === 'function') {
          onThreadMeta(threadId, { ...(chatTopics.canClearParentUnread() ? { unread_count: 0 } : {}), latest_message: realtimeMessage });
        }
        return;
      }
      (async () => {
        try {
          const data = await listArtistGroupChatMessages(threadId, 0, GROUP_CHAT_PAGE_SIZE);
          const rows = Array.isArray(data?.messages) ? data.messages : [];
          if (rows.length) setMessages((prev) => mergeMessages(prev, rows));
        } catch (_) {}
      })();
      if (typeof onThreadsRefresh === 'function') onThreadsRefresh(true);
    };
    const onReadUpdated = (payload = {}) => {
      if (String(pickRealtimeThreadId(payload) || '') !== String(threadId)) return;
      groupPaneDbg('socket:read-updated', {
        threadId,
        readerType: payload?.readerType || null,
        readerId: payload?.readerId || null,
      });
      const readMessageIds = Array.isArray(payload?.readMessageIds)
        ? payload.readMessageIds
        : payload?.read_message_ids;
      if (Array.isArray(readMessageIds) && readMessageIds.length > 0) {
        setMessages((prev) => patchMessageReadCountsByIds(prev, readMessageIds, payload, { scope: 'group' }));
        return;
      }
      loadMessages();
    };
    const onSettings = (payload = {}) => {
      if (String(pickRealtimeThreadId(payload) || '') !== String(threadId)) return;
      groupPaneDbg('socket:settings-updated', { threadId });
      loadThread();
      if (typeof onThreadsRefresh === 'function') onThreadsRefresh(true);
    };
    const onMembership = (payload = {}) => {
      if (String(pickRealtimeThreadId(payload) || '') !== String(threadId)) return;
      groupPaneDbg('socket:membership-changed', { threadId });
      if (typeof onThreadsRefresh === 'function') onThreadsRefresh(true);
      loadThread();
    };
    const onMessageDeleted = (payload = {}) => {
      if (String(pickRealtimeThreadId(payload) || '') !== String(threadId)) return;
      groupPaneDbg('socket:message-deleted', {
        threadId,
        messageId: payload?.messageId || null,
      });
      const deletedIds = Array.isArray(payload?.messageIds) && payload.messageIds.length
        ? payload.messageIds.map((id) => String(id))
        : (payload?.messageId ? [String(payload.messageId)] : []);
      if (deletedIds.length > 0) {
        setMessages((prev) => removeMessages(prev, deletedIds));
      }
      if (typeof onThreadsRefresh === 'function') onThreadsRefresh(true);
    };
    const onAnnouncementsUpdated = (payload = {}) => {
      if (String(pickRealtimeThreadId(payload) || '') !== String(threadId)) return;
      groupPaneDbg('socket:announcements-updated', { threadId });
      refreshAnnouncements({ silent: true }).catch(() => {});
    };
    const onReactionUpdated = (payload = {}) => {
      if (String(pickRealtimeThreadId(payload) || '') !== String(threadId)) return;
      if (!payload?.messageId) return;
      groupPaneDbg('socket:reaction-updated', {
        threadId,
        messageId: payload?.messageId || null,
      });
      refreshMessageReactionDetails(payload.messageId).catch(() => {});
    };

    const handleLinkPreviewReady = makeLinkPreviewReadyHandler(setMessages);

    socket.on('connect', handleConnect);
    socket.on(SOCKET_EVENTS.NEW_GROUP_CHAT_MESSAGE, onNewMessage);
    socket.on(SOCKET_EVENTS.GROUP_CHAT_READ_UPDATED, onReadUpdated);
    socket.on(SOCKET_EVENTS.GROUP_CHAT_SETTINGS_UPDATED, onSettings);
    socket.on(SOCKET_EVENTS.GROUP_CHAT_MEMBERSHIP_CHANGED, onMembership);
    socket.on(SOCKET_EVENTS.GROUP_CHAT_MESSAGE_DELETED, onMessageDeleted);
    socket.on(SOCKET_EVENTS.GROUP_CHAT_ANNOUNCEMENTS_UPDATED, onAnnouncementsUpdated);
    socket.on(SOCKET_EVENTS.GROUP_CHAT_MESSAGE_REACTION_UPDATED, onReactionUpdated);
    socket.on(SOCKET_EVENTS.CHAT_LINK_PREVIEW_READY, handleLinkPreviewReady);

    return () => {
      groupPaneDbg('socket:leave-thread', { threadId });
      socket.emit(SOCKET_EVENTS.LEAVE_GROUP_CHAT_THREAD, threadId);
      socket.off('connect', handleConnect);
      socket.off(SOCKET_EVENTS.NEW_GROUP_CHAT_MESSAGE, onNewMessage);
      socket.off(SOCKET_EVENTS.GROUP_CHAT_READ_UPDATED, onReadUpdated);
      socket.off(SOCKET_EVENTS.GROUP_CHAT_SETTINGS_UPDATED, onSettings);
      socket.off(SOCKET_EVENTS.GROUP_CHAT_MEMBERSHIP_CHANGED, onMembership);
      socket.off(SOCKET_EVENTS.GROUP_CHAT_MESSAGE_DELETED, onMessageDeleted);
      socket.off(SOCKET_EVENTS.GROUP_CHAT_ANNOUNCEMENTS_UPDATED, onAnnouncementsUpdated);
      socket.off(SOCKET_EVENTS.GROUP_CHAT_MESSAGE_REACTION_UPDATED, onReactionUpdated);
      socket.off(SOCKET_EVENTS.CHAT_LINK_PREVIEW_READY, handleLinkPreviewReady);
    };
  }, [
    loadMessages,
    loadThread,
    onThreadMeta,
    onThreadsRefresh,
    refreshAnnouncements,
    refreshMessageReactionDetails,
    setMessages,
    threadId,
  ]);
}
